import { isDefined } from 'shared/typechecks';
import { useAppSelector } from 'slices/store';
import {
  defaultTimeZoneSelector,
  timeZoneHasErrorsSelector,
  timeZoneLoadingSelector,
  timeZonesFetchedSelector,
} from 'slices/timeZone.slice';
import { pickTimeZoneIdentifier } from 'utils/timezone';

/**
 * Resolves the organization's default time zone identifier from global state.
 *
 * @returns
 * `timezone`:
 * - `undefined` while the time zones have not been requested yet, are still
 *   loading, or failed to load. Callers must not convert times then; we never
 *   guess a time zone.
 * - The first of the default time zone's identifiers that this runtime
 *   recognizes, once loaded. `undefined` if it recognizes none of them.
 * - `'UTC'` only when the request succeeded and the server has no default.
 *
 * `loading` and `hasErrors`: mirror the time zone slice so callers
 * `ready` is a boolean indicating the hook produced a usable identifier
 * can render loading and disabled states.
 */
export const useDefaultTimeZone = () => {
  const defaultTimeZone = useAppSelector(defaultTimeZoneSelector);
  const fetched = useAppSelector(timeZonesFetchedSelector);
  const loading = useAppSelector(timeZoneLoadingSelector);
  const hasErrors = useAppSelector(timeZoneHasErrorsSelector);

  let timezone: string | undefined;
  if (isDefined(defaultTimeZone)) {
    timezone = pickTimeZoneIdentifier(defaultTimeZone.identifiers);
  } else if (fetched && !loading && !hasErrors) {
    timezone = 'UTC';
  }

  return { timezone, loading, ready: !!timezone, hasErrors };
};
